import { useState, useEffect } from 'react';
import {
  BarChart3,
  ChevronDown,
  Loader2,
  Save,
  Trash2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MODEL_CATALOG,
  PROVIDER_LABELS,
  OPENAI_QUALITY_OPTIONS,
  type ProviderName,
} from '@/lib/modelCatalog';

// ============================================================================
// Helpers
// ============================================================================

function getModelLabel(provider: ProviderName, modelId: string): string {
  const entry = MODEL_CATALOG[provider]?.find(m => m.id === modelId);
  return entry?.label ?? modelId;
}

// ============================================================================
// Component
// ============================================================================

export const AdminABTestView: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  // All A/B tests (active + archived)
  const [tests, setTests] = useState<Array<{
    id: string;
    is_active: boolean;
    room_prompt_key: string;
    provider_a: ProviderName;
    provider_b: ProviderName;
    model_a: string;
    model_b: string;
    params_a: Record<string, unknown>;
    params_b: Record<string, unknown>;
    prompt_b_content: string;
    description: string;
    created_at: string;
    ended_at: string | null;
  }>>([]);

  // Results grouped by ab_test_id
  const [resultsByTest, setResultsByTest] = useState<Record<string, { a: number; b: number; total: number }>>({});

  // Legacy results (prompt_variant IS NULL)
  const [legacyCount, setLegacyCount] = useState(0);

  // New test form state
  const [selectedRoomKey, setSelectedRoomKey] = useState('');
  const [newPromptB, setNewPromptB] = useState('');
  const [newProviderA, setNewProviderA] = useState<ProviderName>('gemini');
  const [newProviderB, setNewProviderB] = useState<ProviderName>('gemini');
  const [newModelA, setNewModelA] = useState<string>(MODEL_CATALOG.gemini[0].id);
  const [newModelB, setNewModelB] = useState<string>(MODEL_CATALOG.gemini[0].id);
  const [newParamsA, setNewParamsA] = useState<Record<string, unknown>>({});
  const [newParamsB, setNewParamsB] = useState<Record<string, unknown>>({});
  const [newDescription, setNewDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Current standard prompt for the selected room (for display as "Variante A")
  const [standardPrompt, setStandardPrompt] = useState('');

  // Expanded test cards
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());

  // Local edits for B-prompts of active tests (testId -> edited content)
  const [editedPrompts, setEditedPrompts] = useState<Record<string, string>>({});
  const [savingPrompt, setSavingPrompt] = useState<string | null>(null);

  // Available room prompt keys (from prompt_templates)
  const [roomPromptKeys, setRoomPromptKeys] = useState<Array<{ key: string; label: string; content: string }>>([]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        // 1. Load all A/B tests (active first, then archived by date)
        const { data: testData } = await supabase
          .from('ab_test_config')
          .select('*')
          .not('room_prompt_key', 'is', null)
          .order('ended_at', { ascending: true, nullsFirst: true })
          .order('created_at', { ascending: false });
        if (testData) setTests(testData);

        // 2. Load results grouped by ab_test_id
        const { data: selData } = await supabase
          .from('model_selections')
          .select('ab_test_id, prompt_variant');

        const grouped: Record<string, { a: number; b: number; total: number }> = {};
        let legacy = 0;
        for (const row of selData || []) {
          if (!row.ab_test_id) {
            legacy++;
            continue;
          }
          if (!grouped[row.ab_test_id]) grouped[row.ab_test_id] = { a: 0, b: 0, total: 0 };
          grouped[row.ab_test_id].total++;
          if (row.prompt_variant === 'A') grouped[row.ab_test_id].a++;
          if (row.prompt_variant === 'B') grouped[row.ab_test_id].b++;
        }
        setResultsByTest(grouped);
        setLegacyCount(legacy);

        // 3. Load room prompt templates for the dropdown
        const { data: promptData } = await supabase
          .from('prompt_templates')
          .select('key, label, content')
          .eq('is_active', true)
          .like('key', 'room_%')
          .order('label');
        if (promptData) setRoomPromptKeys(promptData);
      } catch (err) {
        console.error('Failed to load A/B test data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // When room key selection changes, load the standard prompt as "Variante A"
  useEffect(() => {
    if (selectedRoomKey) {
      const prompt = roomPromptKeys.find(p => p.key === selectedRoomKey);
      setStandardPrompt(prompt?.content || '(Kein DB-Prompt gefunden — Hardcoded Fallback wird verwendet)');
      // Pre-fill B with A as starting point for editing
      if (!newPromptB && prompt?.content) {
        setNewPromptB(prompt.content);
      }
    } else {
      setStandardPrompt('');
    }
  }, [selectedRoomKey, roomPromptKeys]);

  // Check if a room key already has an active test
  const activeRoomKeys = new Set(tests.filter(t => !t.ended_at).map(t => t.room_prompt_key));

  const handleCreateTest = async () => {
    if (!selectedRoomKey || !newPromptB) return;
    setIsSaving(true);
    try {
      const roomLabel = roomPromptKeys.find(p => p.key === selectedRoomKey)?.label || selectedRoomKey;
      const desc = newDescription || `A/B Test: ${roomLabel}`;

      const { data, error } = await supabase
        .from('ab_test_config')
        .insert({
          is_active: true,
          room_prompt_key: selectedRoomKey,
          provider_a: newProviderA,
          provider_b: newProviderB,
          model_a: newModelA,
          model_b: newModelB,
          params_a: newParamsA,
          params_b: newParamsB,
          prompt_b_content: newPromptB,
          description: desc,
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setTests(prev => [data, ...prev]);
        setSelectedRoomKey('');
        setNewPromptB('');
        setNewDescription('');
        setNewParamsA({});
        setNewParamsB({});
      }
    } catch (err) {
      console.error('Failed to create A/B test:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEndTest = async (testId: string) => {
    const { error } = await supabase
      .from('ab_test_config')
      .update({ is_active: false, ended_at: new Date().toISOString() })
      .eq('id', testId);

    if (!error) {
      setTests(prev => prev.map(t => t.id === testId ? { ...t, is_active: false, ended_at: new Date().toISOString() } : t));
    }
  };

  const handleDeleteLegacy = async () => {
    if (!confirm(`${legacyCount} alte Einträge ohne A/B Test Zuordnung löschen?`)) return;
    await supabase.from('model_selections').delete().is('ab_test_id', null);
    setLegacyCount(0);
  };

  const handleSavePromptB = async (testId: string) => {
    const content = editedPrompts[testId];
    if (content === undefined) return;
    setSavingPrompt(testId);
    try {
      const { error } = await supabase
        .from('ab_test_config')
        .update({ prompt_b_content: content })
        .eq('id', testId);
      if (!error) {
        setTests(prev => prev.map(t => t.id === testId ? { ...t, prompt_b_content: content } : t));
        setEditedPrompts(prev => { const next = { ...prev }; delete next[testId]; return next; });
      }
    } finally {
      setSavingPrompt(null);
    }
  };

  const toggleExpanded = (testId: string) => {
    setExpandedTests(prev => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* === New Test Creation === */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Neuen A/B Test erstellen
            </h2>
            <p className="text-sm text-muted-foreground">
              Wähle einen Raumtyp-Prompt aus und schreibe eine B-Variante zum Testen.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* Room type selector */}
              <div>
                <label className="text-xs text-muted-foreground font-medium">Raumtyp-Prompt</label>
                <select
                  value={selectedRoomKey}
                  onChange={e => { setSelectedRoomKey(e.target.value); setNewPromptB(''); }}
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Raumtyp wählen…</option>
                  {roomPromptKeys.map(p => (
                    <option key={p.key} value={p.key} disabled={activeRoomKeys.has(p.key)}>
                      {p.label} {activeRoomKeys.has(p.key) ? '(Test läuft)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {selectedRoomKey && (
                <>
                  {/* Available placeholders */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-muted-foreground">Platzhalter:</span>
                    {['{{ASPECT_RATIO_INSTRUCTION}}', '{{STYLE_INSTRUCTION}}', '{{ROOM_LABEL}}', '{{FEEDBACK}}'].map(v => (
                      <span key={v} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{v}</span>
                    ))}
                  </div>

                  {/* Variant A pickers */}
                  <div className="p-3 border border-primary/20 rounded-xl bg-primary/5 space-y-2">
                    <p className="text-xs font-bold text-primary">Variante A</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Provider A</label>
                        <select
                          value={newProviderA}
                          onChange={e => {
                            const p = e.target.value as ProviderName;
                            setNewProviderA(p);
                            setNewModelA(MODEL_CATALOG[p][0].id);
                            setNewParamsA({});
                          }}
                          className="w-full border rounded px-2 py-1 mt-1 text-sm"
                        >
                          {(Object.keys(PROVIDER_LABELS) as ProviderName[]).map(p => (
                            <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Modell A</label>
                        <select
                          value={newModelA}
                          onChange={e => setNewModelA(e.target.value)}
                          className="w-full border rounded px-2 py-1 mt-1 text-sm"
                        >
                          {MODEL_CATALOG[newProviderA].map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                      {newProviderA === 'openai' && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Quality A</label>
                          <select
                            value={(newParamsA.quality as string) ?? 'auto'}
                            onChange={e => setNewParamsA({ ...newParamsA, quality: e.target.value })}
                            className="w-full border rounded px-2 py-1 mt-1 text-sm"
                          >
                            {OPENAI_QUALITY_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Variant B pickers */}
                  <div className="p-3 border border-blue-200 rounded-xl bg-blue-50/50 space-y-2">
                    <p className="text-xs font-bold text-blue-600">Variante B</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Provider B</label>
                        <select
                          value={newProviderB}
                          onChange={e => {
                            const p = e.target.value as ProviderName;
                            setNewProviderB(p);
                            setNewModelB(MODEL_CATALOG[p][0].id);
                            setNewParamsB({});
                          }}
                          className="w-full border rounded px-2 py-1 mt-1 text-sm"
                        >
                          {(Object.keys(PROVIDER_LABELS) as ProviderName[]).map(p => (
                            <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground">Modell B</label>
                        <select
                          value={newModelB}
                          onChange={e => setNewModelB(e.target.value)}
                          className="w-full border rounded px-2 py-1 mt-1 text-sm"
                        >
                          {MODEL_CATALOG[newProviderB].map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                      {newProviderB === 'openai' && (
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">Quality B</label>
                          <select
                            value={(newParamsB.quality as string) ?? 'auto'}
                            onChange={e => setNewParamsB({ ...newParamsB, quality: e.target.value })}
                            className="w-full border rounded px-2 py-1 mt-1 text-sm"
                          >
                            {OPENAI_QUALITY_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Side-by-side prompts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Variant A — read-only standard prompt */}
                    <div className="space-y-2 p-4 border border-primary/20 rounded-xl bg-primary/5">
                      <p className="text-sm font-bold text-primary">Variante A (Standard)</p>
                      <textarea
                        value={standardPrompt}
                        readOnly
                        className="w-full h-56 text-[11px] font-mono bg-white/50 border border-border rounded-lg p-2 resize-y leading-relaxed text-muted-foreground cursor-default"
                      />
                    </div>

                    {/* Variant B — editable */}
                    <div className="space-y-2 p-4 border border-blue-200 rounded-xl bg-blue-50/50">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-blue-600">Variante B (Test)</p>
                        <span className="text-[10px] text-muted-foreground">{newPromptB.length} Zeichen</span>
                      </div>
                      <textarea
                        value={newPromptB}
                        onChange={e => setNewPromptB(e.target.value)}
                        placeholder="B-Prompt hier schreiben..."
                        className="w-full h-56 text-[11px] font-mono bg-white border border-border rounded-lg p-2 resize-y focus:outline-none focus:ring-1 focus:ring-blue-400 leading-relaxed"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <input
                    type="text"
                    placeholder="Optionale Beschreibung (z.B. 'Kürzerer Prompt ohne Detailbeschreibung')"
                    value={newDescription}
                    onChange={e => setNewDescription(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                  />

                  {/* Create button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleCreateTest}
                      disabled={isSaving || !newPromptB.trim()}
                      className="gap-2"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Test erstellen & aktivieren
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* === Active Tests === */}
      {tests.filter(t => !t.ended_at).length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Laufende Tests ({tests.filter(t => !t.ended_at).length})
            </h2>
            <div className="space-y-3">
              {tests.filter(t => !t.ended_at).map(test => {
                const res = resultsByTest[test.id] || { a: 0, b: 0, total: 0 };
                const pctA = res.total > 0 ? Math.round((res.a / res.total) * 100) : 0;
                const pctB = res.total > 0 ? Math.round((res.b / res.total) * 100) : 0;
                const isExpanded = expandedTests.has(test.id);
                const roomLabel = roomPromptKeys.find(p => p.key === test.room_prompt_key)?.label || test.room_prompt_key;

                return (
                  <div key={test.id} className="border border-border rounded-xl overflow-hidden">
                    {/* Header — always visible */}
                    <button
                      onClick={() => toggleExpanded(test.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                        <div>
                          <p className="text-sm font-semibold">{roomLabel}</p>
                          <p className="text-xs text-muted-foreground">{test.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-xs text-muted-foreground">{res.total} Auswahlen</span>
                          {res.total > 0 && (
                            <div className="flex gap-2 text-xs font-medium">
                              <span className="text-primary">A: {pctA}%</span>
                              <span className="text-blue-600">B: {pctB}%</span>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={e => { e.stopPropagation(); handleEndTest(test.id); }}
                        >
                          Beenden
                        </Button>
                      </div>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-4 border-t border-border">
                        {/* Bar chart */}
                        {res.total > 0 && (
                          <div className="pt-4 space-y-2">
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-primary">Variante A (Standard)</span>
                                <span className="text-muted-foreground">{res.a} ({pctA}%)</span>
                              </div>
                              <div className="h-6 bg-muted rounded-lg overflow-hidden">
                                <div className="h-full rounded-lg bg-primary/80 transition-all duration-500 flex items-center px-2"
                                  style={{ width: `${Math.max(pctA, 3)}%` }}>
                                  <span className="text-[10px] font-bold text-white">{pctA}%</span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-blue-600">Variante B (Test)</span>
                                <span className="text-muted-foreground">{res.b} ({pctB}%)</span>
                              </div>
                              <div className="h-6 bg-muted rounded-lg overflow-hidden">
                                <div className="h-full rounded-lg bg-blue-500/80 transition-all duration-500 flex items-center px-2"
                                  style={{ width: `${Math.max(pctB, 3)}%` }}>
                                  <span className="text-[10px] font-bold text-white">{pctB}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Prompt details */}
                        <div className="grid grid-cols-2 gap-3 pt-2">
                          <div className="text-xs">
                            <p className="font-medium text-muted-foreground mb-1">
                              Modell A: {getModelLabel(test.provider_a ?? 'gemini', test.model_a)}
                              {test.provider_a === 'openai' && Boolean((test.params_a as Record<string, unknown>)?.quality) && (
                                <> · Quality: {String((test.params_a as Record<string, unknown>).quality)}</>
                              )}
                            </p>
                          </div>
                          <div className="text-xs">
                            <p className="font-medium text-muted-foreground mb-1">
                              Modell B: {getModelLabel(test.provider_b ?? 'gemini', test.model_b)}
                              {test.provider_b === 'openai' && Boolean((test.params_b as Record<string, unknown>)?.quality) && (
                                <> · Quality: {String((test.params_b as Record<string, unknown>).quality)}</>
                              )}
                            </p>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">B-Prompt bearbeiten</p>
                            <span className="text-[10px] text-muted-foreground">{(editedPrompts[test.id] ?? test.prompt_b_content).length} Zeichen</span>
                          </div>
                          <textarea
                            value={editedPrompts[test.id] ?? test.prompt_b_content}
                            onChange={e => setEditedPrompts(prev => ({ ...prev, [test.id]: e.target.value }))}
                            className="w-full h-48 text-[11px] font-mono bg-white border border-border rounded-lg p-2 resize-y focus:outline-none focus:ring-1 focus:ring-blue-400 leading-relaxed"
                          />
                          {editedPrompts[test.id] !== undefined && editedPrompts[test.id] !== test.prompt_b_content && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-1.5 text-xs gap-1.5 h-7"
                              disabled={savingPrompt === test.id}
                              onClick={() => handleSavePromptB(test.id)}
                            >
                              {savingPrompt === test.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              B-Prompt speichern
                            </Button>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                          Erstellt: {new Date(test.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* === Archived Tests === */}
      {tests.filter(t => t.ended_at).length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-foreground mb-4">
              Archivierte Tests ({tests.filter(t => t.ended_at).length})
            </h2>
            <div className="space-y-3">
              {tests.filter(t => t.ended_at).map(test => {
                const res = resultsByTest[test.id] || { a: 0, b: 0, total: 0 };
                const pctA = res.total > 0 ? Math.round((res.a / res.total) * 100) : 0;
                const pctB = res.total > 0 ? Math.round((res.b / res.total) * 100) : 0;
                const isExpanded = expandedTests.has(test.id);
                const roomLabel = roomPromptKeys.find(p => p.key === test.room_prompt_key)?.label || test.room_prompt_key;

                return (
                  <div key={test.id} className="border border-border rounded-xl overflow-hidden opacity-70">
                    <button
                      onClick={() => toggleExpanded(test.id)}
                      className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                        <div>
                          <p className="text-sm font-semibold">{roomLabel}</p>
                          <p className="text-xs text-muted-foreground">{test.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-muted-foreground">{res.total} Auswahlen</span>
                        {res.total > 0 && (
                          <div className="flex gap-2 text-xs font-medium">
                            <span className="text-primary">A: {pctA}%</span>
                            <span className="text-blue-600">B: {pctB}%</span>
                          </div>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(test.created_at).toLocaleDateString('de-DE')} – {new Date(test.ended_at!).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-border">
                        {res.total > 0 && (
                          <div className="pt-3 space-y-2">
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-primary">Variante A</span>
                                <span>{res.a} ({pctA}%)</span>
                              </div>
                              <div className="h-5 bg-muted rounded-lg overflow-hidden">
                                <div className="h-full rounded-lg bg-primary/60" style={{ width: `${Math.max(pctA, 3)}%` }} />
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="font-medium text-blue-600">Variante B</span>
                                <span>{res.b} ({pctB}%)</span>
                              </div>
                              <div className="h-5 bg-muted rounded-lg overflow-hidden">
                                <div className="h-full rounded-lg bg-blue-500/60" style={{ width: `${Math.max(pctB, 3)}%` }} />
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-1">B-Prompt</p>
                          <pre className="text-[10px] font-mono bg-muted/30 rounded-lg p-2 max-h-32 overflow-y-auto whitespace-pre-wrap">{test.prompt_b_content}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* === Legacy Data Cleanup === */}
      {legacyCount > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {legacyCount} alte Einträge ohne A/B Test Zuordnung
                </p>
                <p className="text-xs text-muted-foreground">
                  Stammen aus der Zeit vor dem A/B Test System.
                </p>
              </div>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1.5" onClick={handleDeleteLegacy}>
                <Trash2 className="w-3 h-3" /> Bereinigen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
